/**
 * Process Automation (Copilot Task Assist) demo model.
 *
 * Mirrors the live product behaviour where Copilot runs Cognigy workflows:
 * Copilot asks the customer in the chat, the answers fill the task form and
 * the agent reviews and confirms it. In the demo the Cognigy runtime is
 * replaced by a scripted conversation configured per journey in Simulator-v2.
 */

/** How a workflow is surfaced to the agent (rule execution mode) */
export enum ProcessAutomationInvocation {
  /** Agent has to open it from the task picker or slash search */
  MANUAL = 'manual',
  /** Rule auto runs it without agent confirmation */
  AUTO_RUN = 'autoRun',
}

/** Lifecycle states of a task (workflow instance) */
export enum ProcessAutomationStatus {
  IN_PROGRESS = 'inProgress',
  /** Waiting for agent input */
  BLOCKED = 'blocked',
  PAUSED = 'paused',
  SUCCESS = 'success',
  CANCELLED = 'cancelled',
}

/** One Copilot question / customer answer exchange of a task */
export interface ProcessAutomationConversationTurn {
  id: string;
  /** Question Copilot posts in the chat */
  copilotMessage: string;
  /** Scripted customer answer */
  customerReply: string;
  /** Form fields filled from the answer (field id to value) */
  fills: Record<string, string>;
  /** The answer is off-script: the task needs the agent before continuing */
  needsAttention?: boolean;
}

/** Terminal output: agent-centric summary plus the patron-centric message */
export interface ProcessAutomationOutput {
  summary: string;
  /** Message for the customer, editable by the agent before sending */
  customerMessage?: string;
}

export interface ProcessAutomationStep {
  id: string;
  /** Adaptive Card (v1.6) JSON. Supports dollar-brace token templating. */
  card: Record<string, any>;
}

export interface ProcessAutomationWorkflow {
  id: string;
  /** Read-only bot intent name, as configured in Cognigy */
  intentName: string;
  /** Display name shown to agents */
  name: string;
  description: string;
  invocation: ProcessAutomationInvocation;
  /** Text shown above the form when viewing it before Run */
  formIntro?: string;
  /** Text shown above the filled form when the agent reviews it */
  reviewIntro?: string;
  /** Copilot-led conversation */
  conversation?: ProcessAutomationConversationTurn[];
  steps: ProcessAutomationStep[];
  successOutput: ProcessAutomationOutput;
}

/** Per-journey configuration stored on the Contact */
export interface ProcessAutomationConfig {
  enabled: boolean;
  workflows: ProcessAutomationWorkflow[];
}

/** A single task (workflow instance) */
export interface ProcessAutomationRun {
  runId: string;
  workflowId: string;
  workflowName: string;
  status: ProcessAutomationStatus;
  /** Status to return to when a paused task is resumed */
  statusBeforePause?: ProcessAutomationStatus;
  /** Next conversation turn and which half of it is pending */
  conversationIndex: number;
  turnStage: 'copilot' | 'customer';
  /** Off-script answer received - the agent must resume the task */
  requiresAttention?: boolean;
  /** All answers collected - the agent reviews the filled form */
  isReviewing?: boolean;
  /** Values collected so far - used for templating the form and output */
  collectedValues: Record<string, any>;
  output?: ProcessAutomationOutput;
  startedAt: number;
}

/** Process Automation state of one contact */
export interface ProcessAutomationSession {
  isPickerOpen: boolean;
  pickerQuery: string;
  /** Task picked in the Task Assistant, waiting for Run */
  selectedWorkflowId?: string;
  /** The agent opened "View Form" on the selected task */
  isViewingForm: boolean;
  isMinimized: boolean;
  triggeredWorkflowIds: string[];
  dismissedResultRunIds: string[];
  activeRun?: ProcessAutomationRun;
}

/** States in which a task blocks the start of another interactive task */
export const ACTIVE_RUN_STATUSES: ProcessAutomationStatus[] = [
  ProcessAutomationStatus.IN_PROGRESS,
  ProcessAutomationStatus.BLOCKED,
  ProcessAutomationStatus.PAUSED,
];
